import React, { useState } from "react";

import AlertCircle from "@uui/AlertCircle";
import { keyBy, orderBy } from "lodash/fp";

import { Button, SelectChangeEvent, SxProps } from "@mui/material";

import { CALENDAR_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import ROUTES from "@app/common/routes";
import type {
  RssLinkPlatformOption,
  RssLinkPlatformOptionAlert,
  RssLinkPlatformPreference,
} from "@app/common/types";
import type {
  CalendarProviderId,
  SSOProviderId,
} from "@app/common/types/providerTypes";

import Dropdown, { DropdownMenuItem, DropdownProps } from "@rui/Dropdown";
import { getSelectedFromOptions } from "@rui/Dropdown/utils";
import { Link } from "@rui/Link";
import { Row } from "@rui/Stack";
import { Bold } from "@rui/styles";

import {
  PLATFORM_PREFERENCE_CONFIG,
  PlatformPreferenceConfig,
} from "./constants";
import EnablePlatformModal from "./EnablePlatformModal";

const EMPTY_ENABLE_PLATFORM_OPTIONS = { platform: null, alert: null };

export const PLATFORM_PREFERENCE_OPTIONS = orderBy<PlatformPreferenceConfig>(
  ({ comingSoon = false }) => comingSoon,
  "asc",
  [
    PLATFORM_PREFERENCE_CONFIG.all,
    PLATFORM_PREFERENCE_CONFIG.meet,
    PLATFORM_PREFERENCE_CONFIG.teams,
    PLATFORM_PREFERENCE_CONFIG.zoom,
  ]
);

interface PreferredPlatformSelectorProps
  extends Omit<DropdownProps, "onChange" | "name"> {
  onChange: (platform: RssLinkPlatformPreference) => void;
  onConnectCalendar: (calendarProviderId: CalendarProviderId) => void;
  platformOptions: RssLinkPlatformOption[];
  selected?: RssLinkPlatformPreference;
  sx?: SxProps;
}

const HelperText = ({
  alert,
  onConnectOrReauthProvider,
  platform,
}: {
  alert?: RssLinkPlatformOptionAlert;
  onConnectOrReauthProvider: (
    platform: RssLinkPlatformPreference,
    calendarProviderId: CalendarProviderId,
    providerId: SSOProviderId
  ) => void;
  platform?: RssLinkPlatformPreference;
}) => {
  if (!alert || !platform || platform === "all") return null;

  const { label, providerId, calendarProviderId } =
    PLATFORM_PREFERENCE_CONFIG[platform];

  // none of the error states make any sense without a calendar provider, but appease types
  if (!calendarProviderId) return null;

  const calendarProviderDisplayName =
    CALENDAR_PROVIDERS_BY_ID[calendarProviderId].displayName;

  if (alert === "google_calendar_required" && platform === "meet") {
    return (
      <>
        {label} meetings require a {calendarProviderDisplayName}.{" "}
        <Link to={ROUTES.INTEGRATIONS_GOOGLE_CALENDAR}>
          Update your default calendar to {calendarProviderDisplayName}
        </Link>{" "}
        to proceed.
      </>
    );
  }

  const handleConnectOrReauthorizeCalendar = () =>
    onConnectOrReauthProvider(platform, calendarProviderId, providerId!);

  if (alert === "not_connected") {
    return (
      <>
        {calendarProviderDisplayName} is not connected to Read.{" "}
        <Link onClick={handleConnectOrReauthorizeCalendar}>
          Connect a {calendarProviderDisplayName} account
        </Link>{" "}
        to proceed.
      </>
    );
  }

  if (alert === "token_is_revoked") {
    return (
      <>
        {calendarProviderDisplayName} requires reauthorization.{" "}
        <Link onClick={handleConnectOrReauthorizeCalendar}>
          Reauthorize your account
        </Link>{" "}
        to proceed.
      </>
    );
  }

  if (alert === "missing_required_scope") {
    return (
      <>
        <Link onClick={handleConnectOrReauthorizeCalendar}>
          Grant edit permissions
        </Link>{" "}
        to {calendarProviderDisplayName} to create meetings on this platform.
      </>
    );
  }

  return null;
};

const PreferredPlatformSelector = ({
  onChange,
  onConnectCalendar,
  platformOptions,
  selected,
  sx,
  wrapperSx,
}: PreferredPlatformSelectorProps) => {
  const [pendingUpdateValue, setPendingUpdateValue] =
    useState<RssLinkPlatformPreference | null>(null);
  const [enableOpts, setEnableOpts] = useState<{
    platform: RssLinkPlatformPreference | null;
    alert: RssLinkPlatformOptionAlert | null;
  }>(EMPTY_ENABLE_PLATFORM_OPTIONS);

  const platformOptionByPlatform = keyBy("platform", platformOptions);

  const handleChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value as RssLinkPlatformPreference;
    const platformAlert = platformOptionByPlatform[newValue]?.alert;
    if (platformAlert) {
      setPendingUpdateValue(newValue);
    } else {
      setPendingUpdateValue(null);
      onChange(event.target.value as RssLinkPlatformPreference);
    }
  };

  const handleConnectOrReauthorizeCalendar = (
    platform: RssLinkPlatformPreference,
    calendarProviderId: CalendarProviderId
  ) => {
    onConnectCalendar(calendarProviderId);
  };

  const selectedOrPendingValue = pendingUpdateValue ?? selected;

  const selectedItem = selectedOrPendingValue
    ? getSelectedFromOptions(
        PLATFORM_PREFERENCE_OPTIONS,
        selectedOrPendingValue
      )
    : undefined;

  const selectedItemAlert = selectedOrPendingValue
    ? platformOptionByPlatform[selectedOrPendingValue]?.alert
    : undefined;

  const handleEnableClick =
    (platform: RssLinkPlatformPreference, alert: RssLinkPlatformOptionAlert) =>
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setEnableOpts({ platform, alert });
    };

  return (
    <>
      <Dropdown
        name="preferred-platform-selector"
        placeholder="Select one"
        onChange={handleChange}
        selectedItem={selectedItem}
        error={Boolean(selectedItemAlert)}
        inputProps={{
          helperText: (
            <HelperText
              alert={selectedItemAlert}
              onConnectOrReauthProvider={handleConnectOrReauthorizeCalendar}
              platform={selectedOrPendingValue}
            />
          ),
        }}
        sx={sx}
        wrapperSx={wrapperSx}
      >
        {PLATFORM_PREFERENCE_OPTIONS.map(({ value, label, comingSoon }) => {
          const alertForPlatform =
            platformOptionByPlatform[value]?.alert ?? null;

          return (
            <DropdownMenuItem
              key={value}
              label={
                comingSoon ? (
                  <Row
                    variant="apart"
                    sx={{ fontSize: "0.875rem", width: "100%" }}
                  >
                    <span>{label}</span>
                    <Bold color="shades.neutral.600">Coming soon!</Bold>
                  </Row>
                ) : alertForPlatform ? (
                  <Row
                    variant="apart"
                    sx={{
                      fontSize: "0.875rem",
                      color: "shades.neutral.600",
                      width: "100%",
                    }}
                    spacing={1}
                  >
                    <span>{label}</span>
                    <Button
                      color="error"
                      startIcon={<AlertCircle />}
                      sx={{
                        fontSize: "0.875rem",
                        padding: "2px 4px 2px 8px",
                        "& svg": { fontSize: "14px !important" },
                      }}
                      onClick={handleEnableClick(value, alertForPlatform)}
                    >
                      <span>Enable</span>
                    </Button>
                  </Row>
                ) : (
                  label
                )
              }
              value={value}
              disabled={comingSoon}
              selected={value === selected}
              sx={{
                "&.Mui-disabled": {
                  color: "shades.neutral.400",
                  bgcolor: "shades.neutral.50",
                  opacity: 1,
                },
              }}
            />
          );
        })}
      </Dropdown>
      <EnablePlatformModal
        {...enableOpts}
        onClose={() => setEnableOpts(EMPTY_ENABLE_PLATFORM_OPTIONS)}
        onConnectOrReauthProvider={handleConnectOrReauthorizeCalendar}
      />
    </>
  );
};

export default PreferredPlatformSelector;
